import { useEffect, useState } from 'react';

import { GetAuthToken, useAuth } from '@domain/auth/AuthContext';
import { reportMykissClientError } from '@lib/errors/client';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';

interface IState<Response> {
  data?: Response;
  isLoading: boolean;
  error?: unknown;
}

/**
 * This hook can be used for sending rest requests to Rutilus. It works in a similar way to Apollo's
 * useQuery hook, returung an isLoading value, and a data value once the request is complete. By
 * default it will only run client side (this is usually what you want, as we have no way currently
 * to pass REST fetched data from the server to the client like we do with graphQL).
 */
export function useRutilusRestQuery<Response, Variables>(
  queryFunction: (
    variables: Variables,
    getAuthToken: GetAuthToken,
    signal: AbortSignal,
  ) => Promise<Response>,
  variables: Variables,
  { skip = false, callOnServer = false }: { skip?: boolean; callOnServer?: boolean },
): IState<Response> {
  const [state, setState] = useState<IState<Response>>({ isLoading: !skip });
  const clientHasRendered = useClientHasRendered();
  const { getToken } = useAuth();

  const requestAllowed = callOnServer || clientHasRendered;

  useEffect(() => {
    const controller = new AbortController();
    if (!skip && requestAllowed) {
      const { signal } = controller;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- FIB-49117
      setState({ isLoading: true });
      queryFunction(variables, getToken, signal)
        .then(response => {
          if (controller.signal.aborted) {
            return;
          }
          setState({ isLoading: false, data: response });
        })
        .catch((error: unknown) => {
          // Calling abort signal on a network call is not an error and should not be sent to error trackers
          if (error instanceof Error && error.name === 'AbortError') {
            return;
          }
          reportMykissClientError(error);
          setState({ isLoading: false, error });
        });
    }

    return () => {
      controller.abort();
    };
    // We need to pass the variables obj as a dependency, but that causes a loop, so we stringify it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(variables), skip, requestAllowed]);

  return state;
}
